export const MESSAGE_INSIGHT_STATES = {
  EMPTY: 'empty',
  READY: 'ready',
  LOADING: 'loading',
  ERROR: 'error',
};

const AUDIO_TEXT_KEYS = [
  'transcription',
  'audioTranscription',
  'audio_transcription',
  'sttText',
  'stt_text',
  'transcribedText',
  'transcribed_text',
  'transcript',
  'voiceTranscription',
  'voice_transcription',
  'speechToText',
  'speech_to_text',
];

const AUDIO_STATUS_KEYS = [
  'transcriptionStatus',
  'transcription_status',
  'audioTranscriptionStatus',
  'audio_transcription_status',
  'sttStatus',
  'stt_status',
  'transcriptionState',
  'transcription_state',
];

const OCR_TEXT_KEYS = [
  'ocrText',
  'ocr_text',
  'imageOcrText',
  'image_ocr_text',
  'extractedText',
  'extracted_text',
  'detectedText',
  'detected_text',
  'recognizedText',
  'recognized_text',
  'imageText',
  'image_text',
  'documentText',
  'document_text',
];

const OCR_STATUS_KEYS = [
  'ocrStatus',
  'ocr_status',
  'imageOcrStatus',
  'image_ocr_status',
  'extractionStatus',
  'extraction_status',
  'textExtractionStatus',
  'text_extraction_status',
];

const LOADING_STATUSES = new Set([
  'pending',
  'queued',
  'processing',
  'transcribing',
  'extracting',
  'in_progress',
  'running',
]);

const ERROR_STATUSES = new Set([
  'failed',
  'failure',
  'error',
  'errored',
  'timeout',
  'unavailable',
  'not_available',
]);

const FIELD_CONFIG = {
  audio: {
    textKeys: AUDIO_TEXT_KEYS,
    statusKeys: AUDIO_STATUS_KEYS,
  },
  ocr: {
    textKeys: OCR_TEXT_KEYS,
    statusKeys: OCR_STATUS_KEYS,
  },
};

const nestedMetadataKeys = [
  'contentAttributes',
  'content_attributes',
  'metadata',
  'meta',
  'data',
  'analysis',
  'processing',
];

const normalizeText = value => {
  if (value === null || value === undefined) return '';

  if (Array.isArray(value)) {
    return value.map(normalizeText).filter(Boolean).join('\n');
  }

  if (typeof value === 'object') {
    return normalizeText(
      value.text ??
        value.content ??
        value.value ??
        value.transcription ??
        value.ocrText ??
        value.ocr_text
    );
  }

  return String(value).trim();
};

const normalizeStatus = value => {
  if (value === null || value === undefined) return '';

  if (typeof value === 'object') {
    return normalizeStatus(value.status ?? value.state);
  }

  return String(value).trim().toLowerCase();
};

const compactObjects = objects =>
  objects.filter(
    object => object && typeof object === 'object' && !Array.isArray(object)
  );

const collectSources = ({ contentAttributes = {}, attachment = {} }) => {
  const directSources = compactObjects([contentAttributes, attachment]);
  const nestedSources = directSources.flatMap(source =>
    nestedMetadataKeys.map(key => source[key])
  );

  return compactObjects([...directSources, ...nestedSources]);
};

const readText = (sources, textKeys) => {
  return (
    sources
      .flatMap(source => textKeys.map(key => normalizeText(source[key])))
      .find(Boolean) || ''
  );
};

const readStatus = (sources, textKeys, statusKeys) => {
  const statusFromStatusKeys = sources
    .flatMap(source => statusKeys.map(key => normalizeStatus(source[key])))
    .find(Boolean);

  if (statusFromStatusKeys) return statusFromStatusKeys;

  return (
    sources
      .flatMap(source =>
        textKeys.map(key =>
          typeof source[key] === 'object' ? normalizeStatus(source[key]) : ''
        )
      )
      .find(Boolean) || ''
  );
};

const resolveState = ({ text, status }) => {
  if (text) return MESSAGE_INSIGHT_STATES.READY;
  if (LOADING_STATUSES.has(status)) return MESSAGE_INSIGHT_STATES.LOADING;
  if (ERROR_STATUSES.has(status)) return MESSAGE_INSIGHT_STATES.ERROR;

  return MESSAGE_INSIGHT_STATES.EMPTY;
};

export const readMessageIntelligence = ({
  type,
  contentAttributes,
  attachment,
}) => {
  const config = FIELD_CONFIG[type];

  if (!config) {
    return {
      text: '',
      status: '',
      state: MESSAGE_INSIGHT_STATES.EMPTY,
    };
  }

  const sources = collectSources({ contentAttributes, attachment });
  const text = readText(sources, config.textKeys);
  const status = readStatus(sources, config.textKeys, config.statusKeys);

  return {
    text,
    status,
    state: resolveState({ text, status }),
  };
};
